// External
import _ from "lodash";
import React from "react";

// Design system
import {
  ReloadArrowOutlined,
  FourLinesOutlined,
  TwoLinesOutlined,
  BroomOutlined,
  LanguageOutlined,
  SetupOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";

// Constants
import {
  PROMPT_TYPES,
  LANGUAGE_ACTIONS,
  AI_PROMPTS_ACTION_MAPPING,
  AI_GENERATE_ACTIONS_MAPPING,
} from "./AIGradingPromptBoxConstants";

const {
  REGENERATE,
  SHORTEN,
  ELABORATE,
  SIMPLIFY_LANGUAGE,
  FIX_SPELLING_GRAMMAR,
  IMPROVE_LANGUAGE,
} = AI_GENERATE_ACTIONS_MAPPING;

const { TRANSLATE, DEFAULT_LANGUAGE } = AI_PROMPTS_ACTION_MAPPING;

const DIVIDER = "divider";

const promptsIconMapping = {
  [REGENERATE]: <ReloadArrowOutlined variant="subtle" />,
  [ELABORATE]: <FourLinesOutlined variant="subtle" />,
  [SHORTEN]: <TwoLinesOutlined variant="subtle" />,
  [SIMPLIFY_LANGUAGE]: <BroomOutlined variant="subtle" />,
  [IMPROVE_LANGUAGE]: <PencilLineOutlined variant="subtle" />,
  [FIX_SPELLING_GRAMMAR]: <SetupOutlined variant="subtle" />,
  [TRANSLATE]: <LanguageOutlined variant="subtle" />,
  [DEFAULT_LANGUAGE]: <LanguageOutlined variant="subtle" />,
};

const getAIGradingActions = t => {
  return [
    {
      key: REGENERATE,
      label: t("progressReport:regenerate"),
      icon: promptsIconMapping[REGENERATE],
    },
  ];
};

const getTextGenerationActions = (t, defaultLanguagePreference) => {
  return [
    {
      key: DIVIDER,
      isDivider: true,
    },
    {
      key: SHORTEN,
      label: t("common:shorten"),
      icon: promptsIconMapping[SHORTEN],
    },
    {
      key: ELABORATE,
      label: t("progressReport:elaborate"),
      icon: promptsIconMapping[ELABORATE],
    },
    {
      key: IMPROVE_LANGUAGE,
      label: t("progressReport:improve_language"),
      icon: promptsIconMapping[IMPROVE_LANGUAGE],
    },
    {
      key: SIMPLIFY_LANGUAGE,
      label: t("progressReport:simplify_language"),
      icon: promptsIconMapping[SIMPLIFY_LANGUAGE],
    },
    {
      key: FIX_SPELLING_GRAMMAR,
      label: t("progressReport:fix_spelling_and_grammer"),
      icon: promptsIconMapping[FIX_SPELLING_GRAMMAR],
    },
    {
      key: TRANSLATE,
      label: t("common:translate_to"),
      icon: promptsIconMapping[TRANSLATE],
      isSubMenu: true,
      options: _.map(LANGUAGE_ACTIONS, language => ({
        key: language.code,
        label: language.label,
      })),
    },
    {
      key: DIVIDER,
      isDivider: true,
    },
    {
      key: DEFAULT_LANGUAGE,
      label: t("common:default_language"),
      icon: promptsIconMapping[DEFAULT_LANGUAGE],
      isSubMenu: true,
      default: _.get(defaultLanguagePreference, "label", null) || null,
      options: _.map(LANGUAGE_ACTIONS, language => ({
        key: `${DEFAULT_LANGUAGE}:${language.id}`,
        label: language.label,
      })),
    },
  ];
};

export const getAIGradingMenuOptions = ({
  t,
  promptType,
  defaultLanguagePreference = {},
}) => {
  let menuOptions = [];

  const { TEXT_TOOL_EVALUATION } = PROMPT_TYPES;

  switch (promptType) {
    case TEXT_TOOL_EVALUATION: {
      menuOptions = [
        ...getAIGradingActions(t),
        ...getTextGenerationActions(t, defaultLanguagePreference),
      ];
      break;
    }
    default:
      return [];
  }

  return menuOptions;
};

export const createTextToolQueryVariables = ({
  action,
  sourceText,
  languagePreference,
}) => {
  return {
    action,
    context: {
      language: languagePreference?.label,
      selectedText: sourceText,
    },
    entityType: "GENERIC_TEXT",
    fieldType: "ENHANCE",
    module: "ASSESSMENT_EVALUATION",
  };
};

export const createNonTextToolQueryVariables = ({
  entityId,
  curriculumProgramId,
  assessmentId,
  assessmentToolType,
  userInstructions,
  assessmentToolContext,
  languagePreference,
}) => {
  return {
    entityId,
    entityType: "ASSESSMENT_EVALUATION",
    fieldType: "",
    action: "user_instruction_filled",
    type: "ASSESSMENT_USER_INSTRUCTION",
    curriculumProgramId,
    context: {
      selectedToolID: assessmentToolType,
      userInstructions,
      selectedToolInfo: assessmentToolContext,
      language: languagePreference?.label,
    },
    metadata: {
      assessmentId,
    },
  };
};

export const getPromptBoxQueryVariables = ({
  option,
  sourceText,
  inlineSinglePointRubric,
  assessmentToolType,
  assessmentToolContext,
  entityId,
  curriculumProgramId,
  assessmentId,
  languagePreference,
}) => {
  const { key, parentKey, label } = option || {};

  // Common parameters for text tool queries
  const textToolParams = {
    sourceText,
    languagePreference,
  };

  // Common parameters for non-text tool queries
  const nonTextToolParams = {
    entityId,
    curriculumProgramId,
    assessmentId,
    assessmentToolType,
    assessmentToolContext,
    languagePreference,
  };

  // Handle translation case
  if (parentKey === TRANSLATE) {
    return inlineSinglePointRubric
      ? createNonTextToolQueryVariables({
          ...nonTextToolParams,
          userInstructions: "regenerate",
          languagePreference: { label },
        })
      : createTextToolQueryVariables({
          ...textToolParams,
          action: parentKey,
          languagePreference: { label },
        });
  }

  // Handle specific action keys
  if (key) {
    switch (key) {
      case SHORTEN:
      case ELABORATE:
      case IMPROVE_LANGUAGE:
      case SIMPLIFY_LANGUAGE:
      case FIX_SPELLING_GRAMMAR:
        return inlineSinglePointRubric
          ? createNonTextToolQueryVariables({
              ...nonTextToolParams,
              userInstructions: key,
            })
          : createTextToolQueryVariables({
              ...textToolParams,
              action: key,
            });
      case REGENERATE:
        return createNonTextToolQueryVariables({
          ...nonTextToolParams,
          userInstructions: key,
        });
      default:
        return null;
    }
  }

  return null;
};
